// FILE: src/pages/LoginPage.jsx
// Sign-in (MTO 1.1–1.3, D1): email + password (+ authenticator code when
// the account uses one), then the one-time code we email. Shows attempts
// left, the locked state with support contact (1.3.4), and why the last
// session ended (?reason=idle|replaced|locked|expired).
import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Link as RouterLink, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { startLogin, login, resendLoginCode, clearEndReason } from "../features/authSlice";
import { messageFor, REASON_CODES } from "../i18n/errors";
import SupportContactList from "../components/SupportContactList";
import useSupportChannels from "../hooks/useSupportChannels";
import PublicLayout from "../layouts/PublicLayout";

function attemptsText(n) {
  if (n === null || n === undefined) return "";
  return ` ${n} ${n === 1 ? "attempt" : "attempts"} left before your account is locked.`;
}

// The show/hide button avoids the word "password" in its name: only the
// field itself is labelled Password (screen readers, getByLabel).
const HEADINGS = {
  credentials: { title: "Sign in", sub: "Welcome back. Sign in to continue your course." },
  code: { title: "Check your email", sub: "One more step to keep your account safe." },
  locked: { title: "Account locked", sub: "Student support can help you get back in." }
};

export default function LoginPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [params] = useSearchParams();
  const reasonCode = REASON_CODES[params.get("reason")];

  const [step, setStep] = useState("credentials"); // credentials | code | locked
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null); // { code, attemptsRemaining }
  const [info, setInfo] = useState("");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [needTotp, setNeedTotp] = useState(false);
  const [totp, setTotp] = useState("");

  const [ticket, setTicket] = useState(null);
  const [destination, setDestination] = useState("");
  const [code, setCode] = useState("");
  const [support, setSupport] = useState(null);
  const channels = useSupportChannels(step === "locked" && !support?.phone && !support?.email);

  function handleFailure(payload) {
    const c = payload?.code || "INVALID_CREDENTIALS";
    if (c === "ACCOUNT_LOCKED") {
      setSupport(payload.support || null);
      setStep("locked");
      setError(null);
      return;
    }
    if (c === "TWOFA_TOKEN_REQUIRED") {
      setNeedTotp(true);
      setError(null);
      setInfo(messageFor(c));
      return;
    }
    if (c === "OTP_EXPIRED") {
      setStep("credentials");
      setTicket(null);
      setCode("");
    }
    setInfo("");
    setError({ code: c, attemptsRemaining: payload?.attemptsRemaining });
  }

  async function submitCredentials(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    dispatch(clearEndReason());
    const result = await dispatch(startLogin({ email, password, totp: needTotp ? totp.trim() : "" }));
    setLoading(false);
    if (startLogin.fulfilled.match(result)) {
      setTicket(result.payload.loginTicket);
      setDestination(result.payload.destination);
      setInfo("");
      setStep("code");
    } else handleFailure(result.payload);
  }

  async function submitCode(e) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const result = await dispatch(login({ loginTicket: ticket, code: code.trim() }));
    setLoading(false);
    if (login.fulfilled.match(result)) {
      navigate(result.payload.user.role === "admin" ? "/admin" : "/student");
    } else {
      setCode("");
      handleFailure(result.payload);
    }
  }

  async function resend() {
    setError(null);
    const result = await dispatch(resendLoginCode({ loginTicket: ticket }));
    if (resendLoginCode.fulfilled.match(result)) setInfo(`We sent a new code to ${result.payload.destination}.`);
    else handleFailure(result.payload);
  }

  function restart() {
    setStep("credentials");
    setTicket(null);
    setCode("");
    setError(null);
    setInfo("");
  }

  const heading = HEADINGS[step];

  return (
    <PublicLayout>
      <Card sx={{ borderRadius: 4 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          {step !== "credentials" && (
            <Box
              aria-hidden
              sx={{
                width: 52,
                height: 52,
                borderRadius: 3,
                display: "grid",
                placeItems: "center",
                mb: 2,
                bgcolor: step === "locked" ? "zun.errorTint" : "zun.primaryTint",
                color: step === "locked" ? "error.main" : "primary.main"
              }}
            >
              {step === "locked" ? <LockOutlinedIcon /> : <MarkEmailReadOutlinedIcon />}
            </Box>
          )}
          <Typography variant="h1" sx={{ mb: 1 }}>
            {heading.title}
          </Typography>
          <Typography sx={{ color: "text.secondary", mb: 3 }}>{heading.sub}</Typography>

          {reasonCode && step === "credentials" && !error && (
            <Alert severity={reasonCode === "ACCOUNT_LOCKED" ? "error" : "info"} sx={{ mb: 2 }} role="status">
              {messageFor(reasonCode)}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} role="alert">
              {messageFor(error.code)}
              {attemptsText(error.attemptsRemaining)}
            </Alert>
          )}
          {info && (
            <Alert severity="info" sx={{ mb: 2 }} role="status">
              {info}
            </Alert>
          )}

          {step === "credentials" && (
            <Box component="form" onSubmit={submitCredentials} noValidate>
              <Stack spacing={2.5}>
                <TextField
                  label="Email"
                  type="email"
                  fullWidth
                  required
                  autoComplete="username"
                  inputProps={{ inputMode: "email" }}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <TextField
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword((v) => !v)}
                            aria-label={showPassword ? "Hide what you typed" : "Show what you typed"}
                            aria-pressed={showPassword}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                  }}
                />
                {needTotp && (
                  <TextField
                    label="Authenticator app code"
                    fullWidth
                    autoComplete="one-time-code"
                    inputProps={{ inputMode: "numeric", maxLength: 6, pattern: "[0-9]*" }}
                    value={totp}
                    onChange={(e) => setTotp(e.target.value)}
                  />
                )}
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={loading || !email || !password}
                >
                  {loading ? <CircularProgress size={24} color="inherit" aria-label="Checking" /> : "Continue"}
                </Button>
              </Stack>
              <Stack direction="row" spacing={3} sx={{ mt: 3, flexWrap: "wrap", rowGap: 1 }}>
                <Link component={RouterLink} to="/forgot-password">
                  Forgot your password?
                </Link>
                <Link component={RouterLink} to="/system-requirements">
                  System requirements
                </Link>
              </Stack>
            </Box>
          )}

          {step === "code" && (
            <Box component="form" onSubmit={submitCode} noValidate>
              <Typography sx={{ mb: 2 }}>
                We emailed a 6-digit code to <strong>{destination}</strong>. It expires in 10 minutes.
              </Typography>
              <TextField
                label="Verification code"
                fullWidth
                required
                autoFocus
                autoComplete="one-time-code"
                inputProps={{
                  inputMode: "numeric",
                  maxLength: 6,
                  pattern: "[0-9]*",
                  style: { fontSize: "1.5rem", letterSpacing: "0.35em", fontWeight: 650 }
                }}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                sx={{ mt: 2.5 }}
                disabled={loading || code.trim().length < 6}
              >
                {loading ? <CircularProgress size={24} color="inherit" aria-label="Checking" /> : "Verify and sign in"}
              </Button>
              <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: "wrap", rowGap: 1 }}>
                <Button onClick={resend}>Send a new code</Button>
                <Button onClick={restart}>Use a different account</Button>
              </Stack>
              <Typography sx={{ color: "text.secondary", mt: 2 }}>
                Can't find it? Check your junk or spam folder.
              </Typography>
            </Box>
          )}

          {step === "locked" && (
            <Stack spacing={2} role="alert" data-testid="login-locked">
              <Alert severity="error">{messageFor("ACCOUNT_LOCKED")}</Alert>
              <Typography component="h2" variant="h3">
                Contact student support
              </Typography>
              <SupportContactList contact={support} channels={channels} />
            </Stack>
          )}
        </CardContent>
      </Card>
    </PublicLayout>
  );
}
